import React from 'react';
import { render, screen } from '@testing-library/react';
import CircularProgress from './CircularProgress';

describe('CircularProgress', () => {
  it('renders the percentage label', () => {
    render(<CircularProgress percentage={75} />);
    expect(screen.getByText('75%')).toBeInTheDocument();
  });

  it('renders SVG circles', () => {
    const { container } = render(<CircularProgress percentage={50} />);
    const circles = container.querySelectorAll('circle');
    expect(circles).toHaveLength(2);
  });

  it('renders only trail circle at 0%', () => {
    const { container } = render(<CircularProgress percentage={0} />);
    const circles = container.querySelectorAll('circle');
    expect(circles).toHaveLength(1);
  });

  it('applies custom className', () => {
    const { container } = render(<CircularProgress percentage={50} className="custom" />);
    expect(container.firstChild).toHaveClass('custom');
  });

  it('applies custom size', () => {
    const { container } = render(<CircularProgress percentage={50} size={200} />);
    const el = container.firstChild as HTMLElement;
    expect(el.style.width).toBe('200px');
    expect(el.style.height).toBe('200px');
  });

  describe('contexts', () => {
    const contexts = ['talent', 'company', 'neutral'] as const;

    contexts.forEach(context => {
      it(`renders ${context} context`, () => {
        const { container } = render(<CircularProgress percentage={50} context={context} />);
        expect(container.querySelector(`.${context}`)).toBeInTheDocument();
      });
    });
  });

  describe('clamping', () => {
    it('clamps percentage to 0 when negative', () => {
      render(<CircularProgress percentage={-10} />);
      expect(screen.getByText('0%')).toBeInTheDocument();
      expect(screen.getByRole('progressbar')).toHaveAttribute('aria-valuenow', '0');
    });

    it('clamps percentage to 100 when exceeding', () => {
      render(<CircularProgress percentage={150} />);
      expect(screen.getByText('100%')).toBeInTheDocument();
      expect(screen.getByRole('progressbar')).toHaveAttribute('aria-valuenow', '100');
    });

    it('rounds percentage in display', () => {
      render(<CircularProgress percentage={33.7} />);
      expect(screen.getByText('34%')).toBeInTheDocument();
    });
  });

  describe('accessibility', () => {
    it('has role="progressbar"', () => {
      render(<CircularProgress percentage={50} />);
      expect(screen.getByRole('progressbar')).toBeInTheDocument();
    });

    it('has aria-valuenow', () => {
      render(<CircularProgress percentage={42} />);
      expect(screen.getByRole('progressbar')).toHaveAttribute('aria-valuenow', '42');
    });

    it('has aria-valuemin and aria-valuemax', () => {
      render(<CircularProgress percentage={50} />);
      const progressbar = screen.getByRole('progressbar');
      expect(progressbar).toHaveAttribute('aria-valuemin', '0');
      expect(progressbar).toHaveAttribute('aria-valuemax', '100');
    });

    it('has default aria-label', () => {
      render(<CircularProgress percentage={50} />);
      expect(screen.getByRole('progressbar')).toHaveAttribute('aria-label', 'Progress');
    });

    it('supports custom aria-label', () => {
      render(<CircularProgress percentage={80} label="Profile completeness" />);
      expect(screen.getByRole('progressbar')).toHaveAttribute('aria-label', 'Profile completeness');
    });

    it('hides SVG from screen readers', () => {
      const { container } = render(<CircularProgress percentage={50} />);
      expect(container.querySelector('svg')).toHaveAttribute('aria-hidden', 'true');
    });

    it('hides visual label from screen readers', () => {
      render(<CircularProgress percentage={50} />);
      expect(screen.getByText('50%')).toHaveAttribute('aria-hidden', 'true');
    });
  });
});
